<template>
    <v-container fluid class="rq-page">
        <!-- ================= Header ================= -->
        <div class="rq-header">
            <a class="rq-back" @click="getBackList()">
                <v-icon size="20" color="#2A6F97">mdi-arrow-left</v-icon>
                <span class="ml-2">{{ backText }}</span>
            </a>
            <div class="rq-title-row">
                <div>
                    <h1 class="rq-title">Time Off Request</h1>
                    <div class="rq-subtitle">Details of the submitted leave request</div>
                </div>
                <span v-if="leave_data.status_approve_name" class="rq-status-chip" :class="statusChipClass">
                    <v-icon size="18" class="mr-2" :color="statusIconColor">{{ statusIcon }}</v-icon>
                    {{ leave_data.status_approve_name }}
                </span>
            </div>
        </div>

        <div class="rq-grid">
            <!-- ================= Left column ================= -->
            <div>
                <!-- Employee -->
                <div class="rq-card rq-section">
                    <div class="rq-card-title">Employee</div>
                    <div class="d-flex align-center mt-4">
                        <div class="rq-avatar-lg">
                            <img v-if="image_show" :src="leave_data.images" @error="image_show = ''" />
                            <span v-else>{{ initials }}</span>
                        </div>
                        <div class="ml-5" style="min-width: 0;">
                            <div class="rq-emp-name">{{ fullName || "-" }}</div>
                            <div v-if="employeeCode" class="rq-muted">{{ employeeCode }}</div>
                        </div>
                    </div>

                    <v-divider class="rq-divider my-4"></v-divider>

                    <div class="rq-meta-row">
                        <div class="rq-meta">
                            <v-icon size="24" color="#6B7280">mdi-office-building-outline</v-icon>
                            <div class="ml-3">
                                <div class="rq-meta-label">Department</div>
                                <div class="rq-meta-value">{{ departmentName || "-" }}</div>
                            </div>
                        </div>
                        <div class="rq-meta">
                            <v-icon size="24" color="#6B7280">mdi-briefcase-outline</v-icon>
                            <div class="ml-3">
                                <div class="rq-meta-label">Position</div>
                                <div class="rq-meta-value">{{ positionName || "-" }}</div>
                            </div>
                        </div>
                        <div class="rq-meta">
                            <v-icon size="24" color="#6B7280">mdi-account-outline</v-icon>
                            <div class="ml-3">
                                <div class="rq-meta-label">{{ statusKey === "success" ? "Approved by" : statusKey ===
                                    "danger" ? "Rejected by" : "Current approver" }}</div>
                                <div class="rq-meta-value">{{ currentApproverName || "-" }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Request details -->
                <div class="rq-card rq-section mt-5">
                    <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                        <div class="rq-card-title">Request details</div>
                        <v-spacer></v-spacer>
                        <span class="rq-total-chip">
                            <v-icon size="18" color="#2A6F97" class="mr-2">mdi-clock-outline</v-icon>
                            {{ totalChipText }}
                        </span>
                    </div>

                    <div class="rq-detail-row mt-4">
                        <div class="rq-detail">
                            <v-icon size="24" :color="leaveTypeIcon.color">{{ leaveTypeIcon.icon }}</v-icon>
                            <div class="ml-3">
                                <div class="rq-meta-label">Leave type</div>
                                <div class="rq-meta-value">{{ leave_data.leaveTypeId || "-" }}</div>
                            </div>
                        </div>
                        <div class="rq-detail-sep"></div>
                        <div class="rq-detail">
                            <v-icon size="24" color="#2A6F97">mdi-calendar-blank</v-icon>
                            <div class="ml-3">
                                <div class="rq-meta-label">Date</div>
                                <div class="rq-meta-value">{{ summaryDateText }}</div>
                            </div>
                        </div>
                        <div class="rq-detail-sep"></div>
                        <div class="rq-detail">
                            <v-icon size="24" color="#2A6F97">mdi-clock-outline</v-icon>
                            <div class="ml-3">
                                <div class="rq-meta-label">Duration</div>
                                <div class="rq-meta-value">{{ durationText }}</div>
                            </div>
                        </div>
                        <template v-if="requestedText">
                            <div class="rq-detail-sep"></div>
                            <div class="rq-detail">
                                <v-icon size="24" color="#2A6F97">mdi-file-document-outline</v-icon>
                                <div class="ml-3">
                                    <div class="rq-meta-label">Requested</div>
                                    <div class="rq-meta-value">{{ requestedText }}</div>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="rq-label mt-5">Reason</div>
                    <div class="rq-note-box" :class="{ 'rq-note-empty': !leave_data.description }">{{
                        leave_data.description || "No reason
                        provided." }}</div>

                    <!-- Attachments -->
                    <div class="rq-label mt-5">Attachments</div>
                    <div v-if="oldpreview_list.length > 0" class="rq-preview-list">
                        <div v-for="(item, index) in oldpreview_list" :key="index" class="rq-preview-item"
                            @click="deleteItemConfirm(item)">
                            <img :src="item" />
                            <div class="rq-preview-zoom">
                                <v-icon size="16" color="#FFFFFF">mdi-magnify-plus-outline</v-icon>
                            </div>
                        </div>
                    </div>
                    <div v-else class="rq-muted">No attachments</div>

                    <!-- PDF (เดิม - ซ่อนอยู่ ใช้ตอน exportToPDF) -->
                    <vue-html2pdf v-if="oldpreview_list.length > 0" :show-layout="false" :float-layout="true"
                        :enable-download="true" :preview-modal="false" :paginate-elements-by-height="1100"
                        filename="meeting_vote_report" :pdf-quality="2" :manual-pagination="false" pdf-format="a4"
                        pdf-orientation="portrait" @progress="onProgress($event)"
                        @hasStartedGeneration="hasStartedGeneration()" @hasGenerated="hasGenerated($event)"
                        ref="html2Pdf">
                        <section slot="pdf-content">
                            <v-row>
                                <v-col v-for="(item, index) in oldpreview_list" :key="index" class="d-flex child-flex"
                                    cols="12">
                                    <v-img eager disabled :src="item" class="grey lighten-2 mt-3 mb-5" aspect-ratio="1"
                                        contain>
                                    </v-img>
                                </v-col>
                            </v-row>
                        </section>
                    </vue-html2pdf>
                </div>
            </div>

            <!-- ================= Right column ================= -->
            <div class="rq-side">
                <!-- Status / Decision (read only) -->
                <div class="rq-card rq-section">
                    <div class="rq-card-title">Decision</div>
                    <div class="rq-decision-status mt-4" :class="statusChipClass">
                        <v-icon size="28" :color="statusIconColor">{{ statusIcon }}</v-icon>
                        <div class="ml-3">
                            <div class="rq-decision-title">{{ leave_data.status_approve_name || "-" }}</div>
                            <div class="rq-decision-sub">{{ decisionSubText }}</div>
                        </div>
                    </div>

                    <div class="rq-label mt-5">{{ approverNoteLabel }}</div>
                    <div class="rq-note-box" :class="{ 'rq-note-empty': !approverNote }">{{ approverNote || "No note" }}
                    </div>
                </div>

                <!-- Timeline -->
                <div class="rq-card rq-section mt-5">
                    <div class="rq-card-title mb-4">Request timeline</div>
                    <div class="rq-timeline">
                        <div class="rq-tl-item">
                            <div class="rq-tl-dot rq-tl-done">
                                <v-icon size="16" color="#FFFFFF">mdi-file-document-outline</v-icon>
                            </div>
                            <div class="rq-tl-body">
                                <div><b>Submitted</b><span v-if="requestedText"> · {{ requestedText }}</span></div>
                                <div class="rq-muted">by {{ fullName || "-" }}</div>
                            </div>
                        </div>
                        <div v-for="(step, i) in timelineSteps" :key="i" class="rq-tl-item">
                            <div class="rq-tl-dot" :class="step.dotClass">
                                <v-icon size="16" :color="step.iconColor">{{ step.icon }}</v-icon>
                            </div>
                            <div class="rq-tl-body">
                                <div><b>{{ step.title }}</b><span v-if="step.time"> · {{ step.time }}</span></div>
                                <div class="rq-muted">{{ step.sub }}</div>
                                <div v-if="step.remark" class="rq-tl-remark">“{{ step.remark }}”</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ================= ดูรูปแนบ (เดิม: dialogDelete) ================= -->
        <v-dialog v-model="dialogDelete" max-width="720">
            <div class="rq-image-dialog">
                <div class="d-flex align-center mb-3">
                    <span class="rq-card-title" style="font-size: 16px;">Attachment</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="closeDelete">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <img :src="imageshowpopup" class="rq-image-full" />
            </div>
        </v-dialog>

        <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
        <SuccessDialogPush :status="dialogAdd" :text_color="text_color" :title="title" :message="message"
            :link="link" />
    </v-container>
</template>

<script>
import api from "@/services/api";
import { server, imageUrl } from "@/services/constants";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import SuccessDialog from "@/components/cards/SuccessDialog";
import html2pdf from "html2pdf.js";
import VueHtml2pdf from "vue-html2pdf";
import unity from "@/unity/unity";
import moment from "moment";

export default {
    name: "approve-leave-confirm",
    components: { SuccessDialogPush, SuccessDialog, VueHtml2pdf },
    data(vm) {
        return {
            str_m_time: "",
            end_m_time: "",
            image_show: "",
            day_select: "multiple",
            leave_hours: [
                { text: "1 hour", value: 1 },
                { text: "2 hours", value: 2 },
                { text: "3 hours", value: 3 },
                { text: "Half day", value: 4 },
                { text: "5 hours", value: 5 },
                { text: "6 hours", value: 6 },
                { text: "7 hours", value: 7 },
                { text: "Full day", value: 8 },
            ],
            menu_start: false,
            menu_end: false,
            leave_start_hours: "",
            leave_end_hours: "",
            dialogConfirm: false,
            approve_end_flow_status: false,
            approve_detail_data: [],
            imageshowpopup: "",
            fullName: "",
            employee_id: "",
            dialogDelete: false,
            dialogSave: false,
            dialogReject: false,
            preview: null,
            image: null,
            preview_list: [],
            oldpreview_list: [],
            image_list: [],
            menu: false,
            menu2: false,
            search: "",
            toggleEnable: false,
            selectstatus: "",
            dropdownLeave: [],
            dataAll: [],
            mDataArray: [],
            amountShow: "",
            leave_data: {
                leaveType: 0,
                leaveTypeId: 0,
                approve_id_now: 0,
                startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                description: "",
                description_approve: "",
                status_approve_name: "",
                userId: 0,
                departmentId: 0,
                companyId: 0,
                amount: 0,
                images: null,
                lq_id: 0,
            },
            dateCurrent: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                .toISOString()
                .substr(0, 10),

            dialogAdd: false,
            text_color: "text-h5 green--text text-center",
            title: "green",
            message: "green",
            link: "",
            profileImage: "",
            amountHours: 0, // ชั่วโมงลารวม (ใช้แสดงผล)
            requestRaw: {}, // แถวแรกจาก getLeaveHistoryByIDWaiting
            waitingRows: [], // ทุกแถว (1 แถวต่อผู้อนุมัติ) ใช้ทำ timeline
        };
    },

    watch: {
        dialog(val) {
            val || this.close();
        },
        dialogDelete(val) {
            val || this.closeDelete();
        },
    },
    computed: {
        // ===== UI ใหม่ =====
        backText() {
            return this.$route.params.mode == "approverhistory" ? "Back to approval history" : "Back to time off";
        },
        statusKey() {
            const s = String(this.leave_data.status_approve_name || "").toLowerCase();
            if (s.includes("approve")) return "success";
            if (s.includes("reject")) return "danger";
            if (s.includes("cancel")) return "info";
            return "warning";
        },
        statusChipClass() {
            return "rq-chip-" + this.statusKey;
        },
        statusIcon() {
            return {
                success: "mdi-check-circle-outline",
                danger: "mdi-close-circle-outline",
                info: "mdi-cancel",
                warning: "mdi-clock-outline",
            }[this.statusKey];
        },
        statusIconColor() {
            return { success: "#2E7D32", danger: "#C62828", info: "#2A5F8F", warning: "#E08A1E" }[this.statusKey];
        },
        leaveTypeIcon() {
            const t = String(this.leave_data.leaveTypeId || "").toLowerCase();
            if (t.includes("sick")) return { icon: "mdi-thermometer", color: "#3C8D4F", bg: "#EAF4EC" };
            if (t.includes("vacation")) return { icon: "mdi-palm-tree", color: "#2A6F97", bg: "#EAF2F8" };
            if (t.includes("personal") || t.includes("business"))
                return { icon: "mdi-account-outline", color: "#6B4FA0", bg: "#F0ECF7" };
            return { icon: "mdi-calendar-blank-outline", color: "#6B7280", bg: "#F1F3F5" };
        },
        totalText() {
            const t = Number(this.amountHours) || 0;
            if (t <= 0) return this.amountShow || "-";
            const d = Math.floor(t / 8);
            const h = Math.round((t - d * 8) * 100) / 100;
            if (d === 1 && h === 0) return "1 day";
            const parts = [];
            if (d > 0) parts.push(`${d} ${d === 1 ? "day" : "days"}`);
            if (h > 0) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
            return parts.join(" ");
        },
        summaryDateText() {
            const a = this.displayDate(this.leave_data.startDate);
            if (this.day_select === "single") return a;
            const b = this.displayDate(this.leave_data.endDate);
            return a === b ? a : `${a} - ${b}`;
        },
        // ===== ข้อมูลพนักงาน (แสดงเมื่อ API ส่งมา) =====
        employeeCode() {
            const x = this.requestRaw || {};
            return x.employee_code || "";
        },
        departmentName() {
            const x = this.requestRaw || {};
            return x.department_name || "";
        },
        positionName() {
            const x = this.requestRaw || {};
            return x.position_name || "";
        },
        requestedText() {
            const x = this.requestRaw || {};
            const raw = x.created_at;
            if (!raw) return "";
            const m = moment(raw);
            return m.isValid() ? m.format("D MMM YYYY, HH:mm") : "";
        },
        totalChipText() {
            const t = Number(this.amountHours) || 0;
            const d = Math.floor(t / 8);
            const h = Math.round((t - d * 8) * 100) / 100;
            return `${d} ${d === 1 ? "day" : "days"} · ${h} ${h === 1 ? "hour" : "hours"}`;
        },
        durationText() {
            if (this.day_select === "single") return this.hoursText(this.leave_start_hours);
            if (this.day_select === "multiple")
                return `${this.hoursText(this.leave_start_hours)} / ${this.hoursText(this.leave_end_hours)}`;
            if (this.str_m_time || this.end_m_time) return `${this.str_m_time || "-"} - ${this.end_m_time || "-"}`;
            return this.totalText;
        },
        // ===== ขั้นตอนการอนุมัติ (ใช้ field lad_* ที่ backend ส่งมา) =====
        approvalSteps() {
            const seen = {};
            const steps = [];
            (this.waitingRows || []).forEach((r) => {
                if (r.lad_id === undefined || r.lad_id === null || seen[r.lad_id]) return;
                seen[r.lad_id] = true;
                steps.push(r);
            });
            return steps.sort((a, b) => Number(a.lad_id) - Number(b.lad_id));
        },
        // note ของผู้อนุมัติ "คนล่าสุดที่ตัดสิน" (กรณีมีหลายคน)
        // ไม่มีข้อมูลรายขั้น -> ใช้ค่าเดิม leave_data.description_approve
        approverNote() {
            const steps = this.approvalSteps;
            if (!steps.length) return this.leave_data.description_approve || "";
            const decided = steps.filter((r) => [2, 3].includes(Number(r.lad_status)) && String(r.remark_detail || "").trim());
            return decided.length ? decided[decided.length - 1].remark_detail : "";
        },
        // label บนการ์ด Decision เมื่อมีผู้อนุมัติหลายคน
        approverNoteLabel() {
            const steps = this.approvalSteps;
            const decided = steps.filter((r) => [2, 3].includes(Number(r.lad_status)) && String(r.remark_detail || "").trim());
            if (steps.length > 1 && decided.length) {
                const last = decided[decided.length - 1];
                return `Note from ${this.approverName(last, steps.indexOf(last))}`;
            }
            return "Note from approver";
        },
        currentApproverName() {
            const steps = this.approvalSteps;
            const pending = steps.findIndex((r) => Number(r.lad_status) === 1);
            if (this.statusKey === "warning" && pending !== -1) return this.approverName(steps[pending], pending);
            const done = [...steps].reverse().find((r) => [2, 3].includes(Number(r.lad_status)));
            return done ? this.approverName(done, steps.indexOf(done)) : "";
        },
        decisionSubText() {
            const steps = this.approvalSteps;
            const done = [...steps].reverse().find((r) => [2, 3].includes(Number(r.lad_status)));
            if (this.statusKey === "success" || this.statusKey === "danger") {
                if (!done) return this.statusKey === "success" ? "This request has been approved." : "This request has been rejected.";
                const t = this.fmtTime(done.lad_updated_at);
                return `by ${this.approverName(done, steps.indexOf(done))}${t ? " · " + t : ""}`;
            }
            return this.currentApproverName ? `Waiting for ${this.currentApproverName}` : "Waiting for approval";
        },
        timelineSteps() {
            const steps = this.approvalSteps;
            const finished = this.statusKey === "success" || this.statusKey === "danger";
            if (!steps.length) {
                // ไม่มีข้อมูลรายขั้น -> แสดงตามสถานะรวม
                if (this.statusKey === "success")
                    return [{ title: "Approved", sub: "Request approved", icon: "mdi-check", iconColor: "#FFFFFF", dotClass: "rq-tl-ok", remark: this.leave_data.description_approve }];
                if (this.statusKey === "danger")
                    return [{ title: "Rejected", sub: "Request rejected", icon: "mdi-close", iconColor: "#FFFFFF", dotClass: "rq-tl-bad", remark: this.leave_data.description_approve }];
                return [{ title: "Pending review", sub: "Waiting for approval", icon: "mdi-clock-outline", iconColor: "#FFFFFF", dotClass: "rq-tl-current" }];
            }
            let currentFound = false;
            const out = [];
            steps.forEach((r, i) => {
                const name = this.approverName(r, i);
                const st = Number(r.lad_status);
                if (st === 2) {
                    out.push({ title: "Approved", sub: `by ${name}`, time: this.fmtTime(r.lad_updated_at), remark: r.remark_detail, icon: "mdi-check", iconColor: "#FFFFFF", dotClass: "rq-tl-ok" });
                } else if (st === 3) {
                    out.push({ title: "Rejected", sub: `by ${name}`, time: this.fmtTime(r.lad_updated_at), remark: r.remark_detail, icon: "mdi-close", iconColor: "#FFFFFF", dotClass: "rq-tl-bad" });
                } else if (!finished) {
                    if (!currentFound) {
                        currentFound = true;
                        out.push({ title: "Pending review", sub: `Waiting for ${name}`, icon: "mdi-clock-outline", iconColor: "#FFFFFF", dotClass: "rq-tl-current" });
                    } else {
                        out.push({ title: "Next approver", sub: name, icon: "mdi-account-outline", iconColor: "#9CA3AF", dotClass: "rq-tl-next" });
                    }
                }
                // ใบลาจบแล้ว (อนุมัติ/ปฏิเสธ) ขั้นที่ยังเป็น 1 ไม่ต้องแสดง
            });
            return out;
        },
        initials() {
            const parts = String(this.fullName || "").trim().split(/\s+/).filter(Boolean);
            if (!parts.length) return "?";
            return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
        },
        computedDateFromFormatted() {
            return unity.formatDate(this.leave_data.startDate);
        },
        computedDateToFormatted() {
            return unity.formatDate(this.leave_data.endDate);
        },
    },
    async beforeCreate() {
        this.$store.state.navMenu = true;
    },
    async mounted() {
        await api.checkVersion();
        this.$store.state.global_dialog_push = false;
        this.$store.state.global_dialog = false;

        this.userId = localStorage.getItem(server.USER_ID);

        await this.loadDataLeave();
        this.$hideLoader();
    },

    methods: {
        async closeDelete() {
            this.dialogDelete = false;
        },
        async getBackList() {
            if (this.$route.params.mode == "approverhistory") {
                this.$router.push(`/approver-time-off-history`);
            } else {
                this.$router.push(`/my-time-off`);
            }

        },
        async loadDataLeave() {
            const res_leave = await api.getLeaveHistoryByIDWaiting(this.$route.params.id)
            this.requestRaw = (res_leave.data && res_leave.data[0]) || {};
            this.waitingRows = res_leave.data || [];
            console.log("res_leave.data: ")
            console.log(res_leave.data)

            this.leave_data.leaveTypeId = res_leave.data[0].leave_name;
            this.day_select = res_leave.data[0].type_count;
            this.leave_data.startDate = res_leave.data[0].startdate_s;
            this.leave_start_hours = Number(res_leave.data[0].starthour);
            this.leave_data.endDate = res_leave.data[0].enddate_s;
            this.leave_end_hours = res_leave.data[0].endhour;
            this.leave_data.description = res_leave.data[0].description;
            this.leave_data.description_approve = res_leave.data[0].remark_detail;
            this.leave_data.status_approve_name = res_leave.data[0].approve_name;
            this.str_m_time = res_leave.data[0].t_startdate_s;
            this.end_m_time = res_leave.data[0].t_enddate_s;

            var amount = 0;
            var leave_day = 0;
            var leave_hours = 0;

            amount = Number(res_leave.data[0].amount) / 60;
            leave_day = Math.floor(amount / 8);
            leave_hours = amount - (leave_day * 8);

            this.amountShow = `${leave_day} day ${leave_hours} hours`;
            this.amountHours = amount;

            let setiamggfile = res_leave.data[0].preview_image
                ? res_leave.data[0].preview_image.split(",")
                : [];
            if (setiamggfile[0] == "") {
            } else {
                setiamggfile.forEach((x) => {
                    this.oldpreview_list.push(`${imageUrl}/${x}`);
                });
            }
            // ===== ชื่อและรูปพนักงาน =====
            this.image_show = res_leave.data[0].image;

            if (res_leave.data[0].image == "" || res_leave.data[0].image == null) {
                this.leave_data.images = imageUrl + "/personal.png";
            } else {
                this.leave_data.images = imageUrl + "/" + res_leave.data[0].image;
            }

            if (res_leave.data[0].fullname_eng && res_leave.data[0].fullname_eng.trim() !== "") {
                this.fullName = res_leave.data[0].fullname_eng;
            } else {
                this.fullName = res_leave.data[0].fullname;
            }

        },
        // ===== UI helpers (ใหม่) =====
        // รองรับ YYYY-MM-DD / DD/MM/YYYY / ISO -> 7 Sep 2026
        displayDate(value) {
            if (!value) return "-";
            const s = String(value).trim();
            let m = /^\d{2}\/\d{2}\/\d{4}/.test(s) ? moment(s.substring(0, 10), "DD/MM/YYYY") : moment(s);
            return m.isValid() ? m.format("D MMM YYYY") : s;
        },
        approverName(r, i) {
            const en = String(r.approver_name_eng || "").trim();
            const th = String(r.approver_name_th || "").trim();
            return en || th || `Approver level ${i + 1}`;
        },
        fmtTime(v) {
            if (!v) return "";
            const m = moment(v);
            return m.isValid() ? m.format("D MMM YYYY, HH:mm") : "";
        },
        hoursText(v) {
            const o = this.leave_hours.find((x) => Number(x.value) === Number(v));
            return o ? o.text : "-";
        },
        exportToPDF() {
            this.$refs.html2Pdf.generatePdf();
        },
        setupAlertDialog(status, title, message, text_color) {
            this.title = title;
            this.message = message;
            this.dialogAdd = status;
            this.text_color = text_color;
        },
        setupAlertDialogPush(status, title, message, text_color, link) {
            this.title = title;
            this.message = message;
            this.dialogAdd = status;
            this.text_color = text_color;
            this.link = link;
        },
        previewMultiImage: function (event) {
            var input = event.target;
            var count = input.files.length;
            var index = 0;
            if (input.files) {
                while (count--) {
                    var reader = new FileReader();
                    reader.onload = (e) => {
                        this.preview_list.push(e.target.result);
                    };
                    if (input.files[index].type.split("/")[0] === "image") {
                        this.image_list.push(input.files[index]);
                        reader.readAsDataURL(input.files[index]);
                    }
                    index++;
                }
            }
        },
        deleteItemConfirm(imageurl) {
            // this.indeximage = index;
            this.imageshowpopup = imageurl;
            this.dialogDelete = true;
        },
    },
};
</script>

<style scoped>
.hover-back {
    transition: color 0.3s ease;
    /* เพิ่มการเคลื่อนไหว */
}

.hover-back:hover {
    color: #6edbff !important;
    /* เปลี่ยนเป็นสีน้ำเงินเข้มเมื่อ hover */
}

.v-btn:not(.v-btn--round).v-size--default.buttonblue {
    background-color: #1d2442;
    color: white;
}

.v-textarea:not(.v-textarea--round).v-size--default.buttongreen {
    background-color: #09b534;
    color: white;
}

.textarea {
    border: 2px solid;
    border-color: #09b534;
}

.heightsize55 {
    height: 55px;
}

.heightsize80 {
    height: 80px;
}

.heightsize30 {
    height: 30px;
}

.inputsize {
    font-size: smaller;
}

.btnuploadfile {
    border: 2px solid;
}

.heightsize70formadvance {
    height: 70px;
}
</style>

<style scoped>
/* ================= Page ================= */
.rq-page {
    padding: 16px 32px 32px;
    color: #1F2937;
}

/* ดึงหัวข้อขึ้นไปอยู่ระดับเดียวกับ Header (เหมือนหน้า Request) */
.rq-header {
    margin-top: -72px;
}

.rq-back {
    position: relative;
    z-index: 6;
    /* อยู่เหนือ v-app-bar เพื่อให้คลิกได้ */
    display: inline-flex;
    align-items: center;
    color: #2A6F97 !important;
    font-size: 15px;
    cursor: pointer;
    text-decoration: none;
}

.rq-back:hover {
    text-decoration: underline;
}

.rq-title-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}

.rq-title {
    font-size: 30px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
    margin: 14px 0 0;
}

.rq-subtitle {
    font-size: 15px;
    color: #4B5563;
    margin-top: 6px;
}

.rq-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 28px;
    margin-top: 24px;
    align-items: start;
}

/* ================= Card ================= */
.rq-card {
    background: #FFFFFF;
    border: 1px solid #E8ECF1;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.rq-form-body {
    padding: 24px;
}

.rq-side-card {
    padding: 24px;
}

.rq-card-title {
    font-size: 19px;
    font-weight: 700;
    color: #111827;
}

.rq-muted-sm {
    font-size: 13px;
    color: #6B7280;
}

.rq-label {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
}

/* ================= Status chip ================= */
.rq-status-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
    padding: 6px 14px;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
}

.rq-status-chip-sm {
    padding: 3px 10px;
    font-size: 13px;
    border-radius: 4px;
}

.rq-chip-success {
    background: #E8F3EA;
    color: #2E7D32;
}

.rq-chip-danger {
    background: #FDE8E8;
    color: #C62828;
}

.rq-chip-warning {
    background: #FDF0DC;
    color: #E08A1E;
}

.rq-chip-info {
    background: #E6EEF7;
    color: #2A5F8F;
}

/* ================= Info grid ================= */
.rq-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px 24px;
}

.rq-info-item {
    border: 1px solid #EEF1F4;
    border-radius: 8px;
    padding: 12px 16px;
    background: #FAFBFC;
}

.rq-info-label {
    font-size: 13px;
    color: #6B7280;
    margin-bottom: 4px;
}

.rq-info-value {
    font-size: 15px;
    font-weight: 500;
    color: #111827;
}

.rq-info-sub {
    font-weight: 400;
    color: #4B5563;
}

.rq-type-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

/* ================= Total bar ================= */
.rq-total-bar {
    display: flex;
    align-items: center;
    background: #F2F8FC;
    border: 1px solid #DCEAF4;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 14px;
    color: #374151;
}

.rq-total-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #2A6F97;
    display: flex;
    align-items: center;
    justify-content: center;
}

.rq-accent {
    color: #2A6F97;
    font-weight: 600;
    font-size: 16px;
}

.rq-divider {
    border-color: #EEF1F4 !important;
}

/* ================= Notes ================= */
.rq-note-box {
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    padding: 12px 14px;
    font-size: 14px;
    color: #111827;
    min-height: 48px;
    white-space: pre-wrap;
    word-break: break-word;
}

.rq-note-empty {
    color: #9CA3AF;
    font-style: italic;
}

/* ================= Attachments ================= */
.rq-preview-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.rq-preview-item {
    position: relative;
    width: 96px;
    height: 96px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    overflow: hidden;
    cursor: zoom-in;
}

.rq-preview-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.rq-preview-zoom {
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(17, 24, 39, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
}

.rq-image-dialog {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 16px 20px 20px;
}

.rq-image-full {
    display: block;
    width: 100%;
    max-height: 75vh;
    object-fit: contain;
    background: #F3F4F6;
    border-radius: 8px;
}

/* ================= Right column ================= */
.rq-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #2A6F97;
    color: #FFFFFF;
    font-weight: 600;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.rq-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.rq-emp-name {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rq-sum-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: #374151;
    padding: 8px 0;
}

.rq-sum-row> :last-child {
    text-align: right;
}

.rq-sum-row> :last-child:not(.rq-status-chip) {
    color: #111827;
}

/* ================= Responsive ================= */
@media (max-width: 1100px) {
    .rq-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 600px) {
    .rq-page {
        padding: 8px 12px 24px;
    }

    .rq-header {
        margin-top: 0;
    }

    .rq-info-grid {
        grid-template-columns: 1fr;
    }
}

/* ================= Approve page (เพิ่ม) ================= */
.rq-section {
    padding: 22px 24px;
}

.rq-muted {
    font-size: 14px;
    color: #6B7280;
}

.rq-avatar-lg {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #E6F2F8;
    color: #2A6F97;
    font-weight: 600;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.rq-avatar-lg img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.rq-emp-name {
    font-size: 19px;
}

.rq-meta-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.rq-meta,
.rq-detail {
    display: flex;
    align-items: flex-start;
    min-width: 0;
}

.rq-meta-label {
    font-size: 13px;
    color: #6B7280;
}

.rq-meta-value {
    font-size: 15px;
    font-weight: 500;
    color: #111827;
    margin-top: 2px;
}

.rq-total-chip {
    display: inline-flex;
    align-items: center;
    background: #E6F2F8;
    color: #2A6F97;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
    padding: 6px 12px;
}

.rq-detail-row,
.rq-balance-row {
    display: flex;
    align-items: stretch;
    gap: 20px;
    flex-wrap: wrap;
}

.rq-detail {
    flex: 1 1 0;
    min-width: 140px;
}

.rq-detail-sep {
    width: 1px;
    background: #E5E7EB;
}

.rq-note-box {
    background: #F7F8FA;
}

.rq-balance-main {
    flex: 1.6 1 0;
    display: flex;
    align-items: center;
    min-width: 220px;
}

.rq-balance-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #E6F2F8;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.rq-balance-value {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
}

.rq-balance-item {
    flex: 1 1 0;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

/* ===== Decision ===== */
.rq-input>>>.v-input__slot {
    border-radius: 6px !important;
    background: #FFFFFF !important;
}

.rq-input>>>fieldset {
    border-color: #D1D5DB !important;
}

.rq-input.v-input--is-focused>>>fieldset {
    border-color: #2A6F97 !important;
}

.rq-btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-size: 15px !important;
    font-weight: 600 !important;
    height: 46px !important;
    border-radius: 6px !important;
}

.rq-btn-primary {
    color: #FFFFFF !important;
}

.rq-btn-danger {
    border-color: #E5534B !important;
    color: #D93025 !important;
    background: #FFFFFF;
}

.rq-note-line {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #4B5563;
}

/* ===== Timeline ===== */
.rq-timeline {
    position: relative;
}

.rq-tl-item {
    position: relative;
    display: flex;
    align-items: flex-start;
    padding-bottom: 18px;
}

.rq-tl-item:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 15px;
    top: 32px;
    bottom: 0;
    width: 2px;
    background: #E5E7EB;
}

.rq-tl-item:last-child {
    padding-bottom: 0;
}

.rq-tl-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.rq-tl-done {
    background: #2A6F97;
}

.rq-tl-ok {
    background: #2E7D32;
}

.rq-tl-bad {
    background: #C62828;
}

.rq-tl-current {
    background: #E08A1E;
}

.rq-tl-next {
    background: #F3F4F6;
    border: 1px solid #E5E7EB;
}

.rq-tl-body {
    margin-left: 12px;
    font-size: 14px;
    color: #111827;
    padding-top: 5px;
}

@media (max-width: 600px) {
    .rq-meta-row {
        grid-template-columns: 1fr;
    }

    .rq-detail-sep {
        display: none;
    }
}

/* ===== Waiting page (เพิ่ม) ===== */
.rq-decision-status {
    display: flex;
    align-items: center;
    border-radius: 10px;
    padding: 14px 16px;
}

.rq-decision-title {
    font-size: 17px;
    font-weight: 700;
}

.rq-decision-sub {
    font-size: 13px;
    color: #4B5563;
    margin-top: 2px;
}

.rq-tl-remark {
    margin-top: 4px;
    font-size: 13px;
    color: #374151;
    font-style: italic;
}
</style>